var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Form } from '@tribeplatform/react-components/Form';
import { useI18n } from '@tribeplatform/react-components/i18n';
import { useTribeClient } from '@tribeplatform/react-sdk';
import { ExtraInteractionsEvents, } from '@tribeplatform/react-sdk/types';
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { useSlate } from '../../hooks/index.js';
import { MarkdownBlock } from '../Markdown/Block.js';
import { SelectItemsQueryTypes } from './types.js';
import { useSelectItems } from './useSelectItems.js';
export const SelectBlock = (_a) => {
    var _b, _c;
    var { value: defaultValue, items: uncleanItems, appId, helperText: markdownHelperText, callbackId, disabled, dataCallbackId, shouldCallbackOnChange = false, isSearchable = false, children } = _a, rest = __rest(_a, ["value", "items", "appId", "helperText", "callbackId", "disabled", "dataCallbackId", "shouldCallbackOnChange", "isSearchable", "children"]);
    const { extraContext: { slate: { callback }, }, } = useSlate();
    const { $t } = useI18n();
    const formContext = useFormContext();
    const [loading, setLoading] = React.useState(false);
    const initialValue = useRef(defaultValue);
    const { control: enabled, watch } = formContext || {};
    const [search, setSearch] = useState(undefined);
    const { getValues, reset } = formContext !== null && formContext !== void 0 ? formContext : {};
    const { config } = useTribeClient();
    const { interactionsContext: { useInteractions, interactionEmitter }, } = config !== null && config !== void 0 ? config : {};
    const { items, fetchMore, isFetching } = useSelectItems({
        callbackId: dataCallbackId,
        appId,
        items: uncleanItems,
        getValues,
        useInteractions,
        interactionEmitter,
    });
    useEffect(() => {
        if (typeof search === 'string') {
            fetchMore({ type: SelectItemsQueryTypes.Search, text: search });
        }
    }, [search, fetchMore]);
    const name = (_b = rest === null || rest === void 0 ? void 0 : rest.name) !== null && _b !== void 0 ? _b : 'Select';
    useEffect(() => {
        const errorInteractionListenerHandler = (args) => {
            var _a;
            const fieldName = (_a = args === null || args === void 0 ? void 0 : args.context) === null || _a === void 0 ? void 0 : _a.name;
            if (fieldName === name) {
                toast({
                    title: $t({
                        defaultMessage: 'Something went wrong',
                        id: 'Generics.SomethingWentWrong',
                    }),
                    status: 'error',
                });
                reset(Object.assign(Object.assign({}, getValues()), { [name]: initialValue.current }));
            }
        };
        if (interactionEmitter) {
            interactionEmitter.on(ExtraInteractionsEvents.InteractionError, errorInteractionListenerHandler);
        }
        return () => {
            if (interactionEmitter) {
                interactionEmitter.removeListener(ExtraInteractionsEvents.InteractionError, errorInteractionListenerHandler);
            }
        };
    }, [interactionEmitter, reset, rest === null || rest === void 0 ? void 0 : rest.name, getValues, name, $t]);
    const value = enabled ? watch(rest.name, defaultValue) : defaultValue;
    useEffect(() => {
        if (!initialValue.current) {
            initialValue.current = value;
        }
    }, [value]);
    const helperText = typeof markdownHelperText === 'string' ? (React.createElement(MarkdownBlock, { text: markdownHelperText })) : (markdownHelperText);
    if (isSearchable) {
        if (enabled) {
            return (React.createElement(Form.SearchableSelect, Object.assign({}, rest, { name: name, loading: isFetching, onChange: value => {
                    var _a, _b;
                    if (callbackId && shouldCallbackOnChange && value !== '') {
                        interactionEmitter.emit(ExtraInteractionsEvents.InteractionCalled, {});
                        callback(callbackId, {
                            data: {
                                value,
                                name,
                                label: (_b = (_a = items.find(item => item.value === value)) === null || _a === void 0 ? void 0 : _a.text) !== null && _b !== void 0 ? _b : '',
                                formValues: typeof getValues === 'function' ? getValues() : {},
                            },
                        });
                    }
                }, onInputChange: setSearch, options: items.map(item => {
                    var _a, _b;
                    return ({
                        value: (_a = item === null || item === void 0 ? void 0 : item.value) === null || _a === void 0 ? void 0 : _a.toString(),
                        key: (_b = item === null || item === void 0 ? void 0 : item.value) === null || _b === void 0 ? void 0 : _b.toString(),
                        text: item.text,
                    });
                }), hideClearIcon: true, helperText: helperText })));
        }
        return (React.createElement(FormControl.SearchableSelect, Object.assign({}, rest, { loading: isFetching, value: value, onChange: value => {
                var _a, _b;
                if (callbackId && shouldCallbackOnChange) {
                    callback(callbackId, {
                        data: {
                            value,
                            label: (_b = (_a = items.find(item => item.value === value)) === null || _a === void 0 ? void 0 : _a.text) !== null && _b !== void 0 ? _b : '',
                            formValues: getValues(),
                        },
                    });
                }
            }, onInputChange: setSearch, options: items.map(item => {
                var _a, _b;
                return ({
                    value: (_a = item === null || item === void 0 ? void 0 : item.value) === null || _a === void 0 ? void 0 : _a.toString(),
                    key: (_b = item === null || item === void 0 ? void 0 : item.value) === null || _b === void 0 ? void 0 : _b.toString(),
                    text: item.text,
                });
            }), hideClearIcon: true, helperText: helperText })));
    }
    if (enabled) {
        return (React.createElement(Form.Select, Object.assign({ name: (_c = rest === null || rest === void 0 ? void 0 : rest.name) !== null && _c !== void 0 ? _c : 'Select', onChange: async (value) => {
                if (disabled !== true && callbackId) {
                    setLoading(true);
                    try {
                        await callback(callbackId, { data: { value } });
                    }
                    catch (_a) { }
                    setLoading(false);
                }
            } }, rest, { helperText: helperText, items: items, disabled: loading || disabled })));
    }
    return (React.createElement(FormControl.Select, Object.assign({}, rest, { helperText: helperText, items: items, value: value, onChange: async (value) => {
            if (disabled !== true && callbackId) {
                setLoading(true);
                try {
                    await callback(callbackId, { data: { value } });
                }
                catch (_a) { }
                setLoading(false);
            }
        }, disabled: loading || disabled })));
};
//# sourceMappingURL=Block.js.map